import { TOOL_DEFINITIONS } from 'lightweight-charts-drawing';

/**
 * Campo extra editable en el diálogo de settings de un dibujo.
 * `target` indica si el valor vive en `drawing.style` o en `drawing.options`.
 */
export interface SettingsFieldDef {
  key: string;
  label: string;
  kind: 'number' | 'text' | 'textarea' | 'checkbox' | 'color' | 'levels' | 'select';
  target: 'style' | 'options';
  options?: Array<{ value: any; label: string }>;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Metadata de UI propia (labels ES, iconos, agrupación estilo TradingView).
 * El resto (requiredAnchors, factory) viene del registry del plugin.
 */
export interface ToolUiMeta {
  type: string;
  label: string;
  icon: string;
  /** points (default) | freehand (drag) | multipoint (click..doble-click) */
  creationMode?: 'points' | 'freehand' | 'multipoint';
  /** campo de options donde vive el texto del dibujo */
  textField?: 'text' | 'note';
  /** el tipo usa relleno (muestra control de fill en la floating toolbar) */
  hasFill?: boolean;
  /** campos extra del diálogo de settings */
  settingsFields?: SettingsFieldDef[];
  /** ocultar del menú (tools que rendericen mal en la versión actual del plugin) */
  hidden?: boolean;
}

export interface ToolGroup {
  id: string;
  label: string;
  icon: string;
  tools: ToolUiMeta[];
}

/** Paleta de colores estilo TradingView (filas de tonos). */
export const TV_PALETTE: string[][] = [
  ['#FFFFFF', '#E0E3EB', '#B2B5BE', '#787B86', '#50535E', '#2A2E39', '#131722', '#000000'],
  ['#F23645', '#FF9800', '#FFEB3B', '#4CAF50', '#089981', '#00BCD4', '#2962FF', '#673AB7'],
  ['#FCCBCD', '#FFE0B2', '#FFF9C4', '#C8E6C9', '#ACE5DC', '#B2EBF2', '#BBD9FB', '#D1C4E9'],
  ['#FAA1A4', '#FFCC80', '#FFF59D', '#A5D6A7', '#70CCBD', '#80DEEA', '#90BFF9', '#B39DDB'],
  ['#DD2E3E', '#F57C00', '#FBC02D', '#388E3C', '#056656', '#0097A7', '#1848CC', '#512DA8'],
  ['#B22833', '#E65100', '#F57F17', '#1B5E20', '#00332A', '#006064', '#0C3299', '#311B92'],
];

// ── Campos de settings reutilizables ──────────────────────────────────────

const FIB_FIELDS: SettingsFieldDef[] = [
  { key: 'levels', label: 'Niveles', kind: 'levels', target: 'options' },
  { key: 'showPrices', label: 'Mostrar precios', kind: 'checkbox', target: 'options' },
  { key: 'showPercentages', label: 'Mostrar porcentajes', kind: 'checkbox', target: 'options' },
  { key: 'extendLines', label: 'Extender líneas', kind: 'checkbox', target: 'options' },
  { key: 'reverseDirection', label: 'Invertir', kind: 'checkbox', target: 'options' },
];

const POSITION_FIELDS: SettingsFieldDef[] = [
  { key: 'quantity', label: 'Cantidad', kind: 'number', target: 'options', min: 0 },
  { key: 'accountSize', label: 'Tamaño de cuenta', kind: 'number', target: 'options', min: 0 },
  { key: 'showPrices', label: 'Mostrar precios', kind: 'checkbox', target: 'options' },
  { key: 'showPercentage', label: 'Mostrar %', kind: 'checkbox', target: 'options' },
  { key: 'showRiskReward', label: 'Riesgo/Beneficio', kind: 'checkbox', target: 'options' },
  { key: 'showPnL', label: 'Mostrar P&L', kind: 'checkbox', target: 'options' },
];

function textFields(field: 'text' | 'note' = 'text'): SettingsFieldDef[] {
  return [
    { key: field, label: 'Texto', kind: 'textarea', target: 'options' },
    { key: 'fontSize', label: 'Tamaño de fuente', kind: 'number', target: 'options', min: 8, max: 48 },
    { key: 'backgroundColor', label: 'Color de fondo', kind: 'color', target: 'options' },
  ];
}

const EXTEND_FIELDS: SettingsFieldDef[] = [
  { key: 'extendLeft', label: 'Extender a la izquierda', kind: 'checkbox', target: 'options' },
  { key: 'extendRight', label: 'Extender a la derecha', kind: 'checkbox', target: 'options' },
];

// ── Catálogo por grupos, en el orden de la toolbar de TradingView ─────────

export const DRAWING_TOOL_GROUPS: ToolGroup[] = [
  {
    id: 'lines', label: 'Líneas', icon: 'bi bi-slash-lg',
    tools: [
      { type: 'trend-line', label: 'Línea de tendencia', icon: 'bi bi-slash-lg', settingsFields: EXTEND_FIELDS },
      { type: 'ray', label: 'Rayo', icon: 'bi bi-arrow-up-right' },
      { type: 'info-line', label: 'Línea de información', icon: 'bi bi-info-circle', settingsFields: [
        { key: 'showDistance', label: 'Mostrar distancia', kind: 'checkbox', target: 'options' },
        { key: 'showAngle', label: 'Mostrar ángulo', kind: 'checkbox', target: 'options' },
        { key: 'showPriceChange', label: 'Cambio de precio', kind: 'checkbox', target: 'options' },
        { key: 'showPercentChange', label: 'Cambio %', kind: 'checkbox', target: 'options' },
        { key: 'showBars', label: 'Nº de barras', kind: 'checkbox', target: 'options' },
      ] },
      { type: 'extended-line', label: 'Línea extendida', icon: 'bi bi-arrows-expand-vertical' },
      { type: 'trend-angle', label: 'Ángulo de tendencia', icon: 'bi bi-compass' },
      { type: 'horizontal-line', label: 'Línea horizontal', icon: 'bi bi-dash-lg' },
      { type: 'horizontal-ray', label: 'Rayo horizontal', icon: 'bi bi-arrow-right' },
      { type: 'vertical-line', label: 'Línea vertical', icon: 'bi bi-grip-vertical' },
      { type: 'cross-line', label: 'Línea cruzada', icon: 'bi bi-plus-lg' },
    ],
  },
  {
    id: 'channels', label: 'Canales y horquillas', icon: 'bi bi-distribute-vertical',
    tools: [
      { type: 'parallel-channel', label: 'Canal paralelo', icon: 'bi bi-distribute-vertical', hasFill: true },
      { type: 'regression-trend', label: 'Tendencia de regresión', icon: 'bi bi-graph-up', hasFill: true },
      { type: 'flat-top-bottom', label: 'Techo/suelo plano', icon: 'bi bi-align-top', hasFill: true },
      { type: 'disjoint-channel', label: 'Canal disjunto', icon: 'bi bi-layout-split', hasFill: true },
      { type: 'andrews-pitchfork', label: 'Horquilla de Andrews', icon: 'bi bi-share', hasFill: true },
      { type: 'schiff-pitchfork', label: 'Horquilla de Schiff', icon: 'bi bi-share-fill', hasFill: true },
      { type: 'modified-schiff-pitchfork', label: 'Horquilla Schiff modificada', icon: 'bi bi-diagram-2', hasFill: true },
      { type: 'inside-pitchfork', label: 'Horquilla interior', icon: 'bi bi-diagram-3', hasFill: true },
      { type: 'pitchfan', label: 'Abanico de horquilla', icon: 'bi bi-fan', hasFill: true },
    ],
  },
  {
    id: 'fib-gann', label: 'Fibonacci y Gann', icon: 'bi bi-bezier',
    tools: [
      { type: 'fib-retracement', label: 'Retroceso de Fibonacci', icon: 'bi bi-list-ol', hasFill: true, settingsFields: FIB_FIELDS },
      { type: 'fib-extension', label: 'Extensión de Fibonacci', icon: 'bi bi-arrows-angle-expand', hasFill: true, settingsFields: FIB_FIELDS },
      { type: 'fib-channel', label: 'Canal de Fibonacci', icon: 'bi bi-bezier', hasFill: true },
      { type: 'fib-time-zone', label: 'Zonas temporales Fib', icon: 'bi bi-columns', hasFill: true },
      { type: 'fib-time-extension', label: 'Fib temporal de tendencia', icon: 'bi bi-columns-gap', hasFill: true },
      { type: 'fib-speed-fan', label: 'Abanico de velocidad Fib', icon: 'bi bi-pie-chart', hasFill: true },
      { type: 'fib-wedge', label: 'Cuña de Fibonacci', icon: 'bi bi-triangle-half', hasFill: true },
      { type: 'fib-circles', label: 'Círculos de Fibonacci', icon: 'bi bi-circle', hasFill: true },
      { type: 'fib-spiral', label: 'Espiral de Fibonacci', icon: 'bi bi-hurricane' },
      { type: 'fib-arcs', label: 'Arcos de Fibonacci', icon: 'bi bi-rainbow', hasFill: true },
      { type: 'gann-box', label: 'Caja de Gann', icon: 'bi bi-grid-3x3', hasFill: true },
      { type: 'gann-square-fixed', label: 'Cuadrado de Gann fijo', icon: 'bi bi-grid', hasFill: true },
      { type: 'gann-square', label: 'Cuadrado de Gann', icon: 'bi bi-grid-fill', hasFill: true },
      { type: 'gann-fan', label: 'Abanico de Gann', icon: 'bi bi-sunrise', hasFill: true },
    ],
  },
  {
    id: 'patterns', label: 'Patrones y proyección', icon: 'bi bi-graph-up-arrow',
    tools: [
      { type: 'bars-pattern', label: 'Patrón de barras', icon: 'bi bi-bar-chart-steps' },
      { type: 'long-position', label: 'Posición larga', icon: 'bi bi-graph-up-arrow', hasFill: true, settingsFields: POSITION_FIELDS },
      { type: 'short-position', label: 'Posición corta', icon: 'bi bi-graph-down-arrow', hasFill: true, settingsFields: POSITION_FIELDS },
      { type: 'forecast', label: 'Pronóstico', icon: 'bi bi-lightning', hasFill: true },
      { type: 'projection', label: 'Proyección', icon: 'bi bi-box-arrow-up-right', hasFill: true },
    ],
  },
  {
    id: 'shapes', label: 'Pinceles y formas', icon: 'bi bi-pencil',
    tools: [
      { type: 'brush', label: 'Pincel', icon: 'bi bi-brush', creationMode: 'freehand' },
      { type: 'highlighter', label: 'Resaltador', icon: 'bi bi-highlighter', creationMode: 'freehand' },
      { type: 'arrow', label: 'Flecha', icon: 'bi bi-arrow-up-right-circle' },
      { type: 'arrow-marker', label: 'Marcador de flecha', icon: 'bi bi-cursor' },
      { type: 'arrow-mark-up', label: 'Flecha arriba', icon: 'bi bi-arrow-up-circle' },
      { type: 'arrow-mark-down', label: 'Flecha abajo', icon: 'bi bi-arrow-down-circle' },
      { type: 'rectangle', label: 'Rectángulo', icon: 'bi bi-square', hasFill: true },
      { type: 'rotated-rectangle', label: 'Rectángulo rotado', icon: 'bi bi-box', hasFill: true },
      { type: 'ellipse', label: 'Elipse', icon: 'bi bi-egg', hasFill: true },
      { type: 'circle', label: 'Círculo', icon: 'bi bi-circle-fill', hasFill: true },
      { type: 'triangle', label: 'Triángulo', icon: 'bi bi-triangle', hasFill: true },
      { type: 'arc', label: 'Arco', icon: 'bi bi-bezier2', hasFill: true },
      { type: 'curve', label: 'Curva', icon: 'bi bi-bezier2' },
      { type: 'double-curve', label: 'Curva doble', icon: 'bi bi-infinity' },
      { type: 'path', label: 'Ruta', icon: 'bi bi-vector-pen', creationMode: 'multipoint' },
      { type: 'polyline', label: 'Polilínea', icon: 'bi bi-bounding-box-circles', creationMode: 'multipoint', hasFill: true },
    ],
  },
  {
    id: 'annotations', label: 'Texto y anotaciones', icon: 'bi bi-type',
    tools: [
      { type: 'text-annotation', label: 'Texto', icon: 'bi bi-type', textField: 'text', settingsFields: textFields() },
      { type: 'anchored-text', label: 'Texto anclado', icon: 'bi bi-textarea-t', textField: 'text', settingsFields: textFields() },
      { type: 'note', label: 'Nota', icon: 'bi bi-sticky', textField: 'text', settingsFields: textFields() },
      { type: 'price-note', label: 'Nota de precio', icon: 'bi bi-tag', textField: 'note', settingsFields: textFields('note') },
      { type: 'comment', label: 'Comentario', icon: 'bi bi-chat-text', textField: 'text', settingsFields: textFields() },
      { type: 'callout', label: 'Bocadillo', icon: 'bi bi-chat-square-text', textField: 'text', settingsFields: textFields() },
      { type: 'price-label', label: 'Etiqueta de precio', icon: 'bi bi-tags' },
      { type: 'signpost', label: 'Señal', icon: 'bi bi-signpost', textField: 'text', settingsFields: [
        ...textFields(),
        { key: 'direction', label: 'Dirección', kind: 'select', target: 'options', options: [
          { value: 'left', label: 'Izquierda' }, { value: 'right', label: 'Derecha' },
        ] },
      ] },
      { type: 'flag-mark', label: 'Bandera', icon: 'bi bi-flag' },
      { type: 'pin', label: 'Pin', icon: 'bi bi-geo-alt' },
      { type: 'table', label: 'Tabla', icon: 'bi bi-table' },
    ],
  },
  {
    id: 'measure', label: 'Medición', icon: 'bi bi-rulers',
    tools: [
      { type: 'date-price-range', label: 'Rango fecha y precio', icon: 'bi bi-rulers', hasFill: true },
      { type: 'price-range', label: 'Rango de precio', icon: 'bi bi-arrows-vertical', hasFill: true },
      { type: 'date-range', label: 'Rango de fecha', icon: 'bi bi-arrows', hasFill: true },
    ],
  },
];

// ── Índices y helpers ─────────────────────────────────────────────────────

const META_BY_TYPE = new Map<string, ToolUiMeta>();
for (const group of DRAWING_TOOL_GROUPS) {
  for (const tool of group.tools) META_BY_TYPE.set(tool.type, tool);
}

const REQUIRED_ANCHORS_BY_TYPE = new Map<string, number>(
  (TOOL_DEFINITIONS as Array<{ type: string; requiredAnchors: number }>).map(d => [d.type, d.requiredAnchors])
);

export function getToolMeta(type: string): ToolUiMeta | undefined {
  return META_BY_TYPE.get(type);
}

export function getRequiredAnchors(type: string): number {
  return REQUIRED_ANCHORS_BY_TYPE.get(type) ?? 2;
}

export function getToolLabel(type: string): string {
  return META_BY_TYPE.get(type)?.label ?? type;
}

export function getToolIcon(type: string): string {
  return META_BY_TYPE.get(type)?.icon ?? 'bi bi-slash-lg';
}

/** Consistencia catálogo ↔ registry del plugin (solo avisa en consola). */
export function validateCatalog(): void {
  const registryTypes = new Set(REQUIRED_ANCHORS_BY_TYPE.keys());
  for (const type of META_BY_TYPE.keys()) {
    if (!registryTypes.has(type)) console.warn(`[chart-drawing] tool '${type}' no existe en el registry del plugin`);
  }
  for (const type of registryTypes) {
    if (!META_BY_TYPE.has(type)) console.warn(`[chart-drawing] tool '${type}' del plugin no está en el catálogo UI`);
  }
}
